import styled from "styled-components";
import { useState } from "react";
import Button from "./ActionButton";
import { IonIcon } from "@ionic/react";
import { filter } from "ionicons/icons";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  margin-bottom: 1.8rem;
`;

const FilterValuesContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 2.4rem 1.2rem;
  width: 100%;
  align-items: flex-start;
  margin-top: 1.5rem;
`;

const FilterGroup = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 120px;
  margin-bottom: 1rem;
`;

const FilterTitle = styled.div`
  font-size: 1.2rem;
  font-weight: bold;
  margin-bottom: 0.5rem;
  color: #ffffff;
  text-transform: uppercase;
`;

const FilterValues = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.7rem;
`;

const Filter = styled.div`
  background-color: ${({ selected }) => (selected ? "#d97706" : "#f4d6b4")};
  color: ${({ selected }) => (selected ? "#fff" : "#d97706")};
  border: 2px solid #d97706;
  padding: 5px 12px;
  white-space: nowrap;
  width: min-content;
  border-radius: 20px;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 300ms ease-in-out;
  font-weight: 500;
  &:hover {
    background-color: #d97706;
    color: #ffffff;
  }
`;

const FilterButton = styled.button`
  background: #d97706;
  color: #fff;
  border: none;
  border-radius: 20px;
  padding: 7px 18px;
  font-size: 1.3rem;
  font-weight: 600;
  cursor: pointer;
  margin-left: 1.5rem;
  transition: background 200ms;
  align-self: flex-end;
  &:hover {
    background: #b45309;
  }
`;

export default function ThemeFilter({
  filters = {},
  onApplyFilters,
  initialSelected = {},
  filterButtonText = "Filtrar temas",
  filterLabels = {},
  filterMultiSelect = {},
}) {
  // Helper to get value and label from a filter item
  const getValue = (item) => (typeof item === "object" ? item.value : item);
  const getLabel = (item) => (typeof item === "object" ? item.label : item);

  const [selected, setSelected] = useState(() => {
    const obj = {};
    Object.keys(filters).forEach((key) => {
      obj[key] = new Set((initialSelected[key] || []).map(getValue));
    });
    return obj;
  });

  const [showFilters, setShowFilters] = useState(
    Object.keys(initialSelected).length > 0
  );

  /**
   * Handles toggling the selection of a filter value.
   * It updates the selected state based on whether the filter is multi-select or single-select.
   * If multi-select, it adds/removes the value from the set.
   * If single-select, it clears the set and adds the new value if not already selected.
   * @param {*} filterName
   * @param {*} value
   */
  const handleToggle = (filterName, value) => {
    setSelected((prev) => {
      const next = { ...prev };
      const isMulti = filterMultiSelect[filterName] !== false; // default true
      const set = new Set(next[filterName] || []);
      if (isMulti) {
        if (set.has(value)) set.delete(value);
        else set.add(value);
        next[filterName] = set;
      } else {
        // Single select: only one value allowed
        if (set.has(value)) {
          set.clear();
        } else {
          set.clear();
          set.add(value);
        }
        next[filterName] = set;
      }
      return next;
    });
  };

  /**
   *
   * @returns A string of URL parameters based on the selected filters.
   * It constructs a query string where each filter type is represented by its key,
   * and the selected values are joined by commas.
   * For example, if 'categories' has 'Açougue' and 'Padaria', it returns 'categories=Açougue,Padaria'.
   */
  const buildUrlParams = () => {
    const params = [];
    Object.entries(selected).forEach(([key, set]) => {
      if (set.size > 0) {
        params.push(
          `${encodeURIComponent(key)}=${Array.from(set)
            .map(encodeURIComponent)
            .join(",")}`
        );
      }
    });
    return params.join("&");
  };

  /**
   * Handles clearing all filters and resetting the selected state.
   * It resets the selected filters to an empty set for each filter type,
   * hides the filter list, and calls the onApplyFilters callback with an empty string.
   * This effectively clears all applied filters.
   */
  const handleClearFilters = () => {
    const cleared = {};
    Object.keys(filters).forEach((key) => {
      cleared[key] = new Set();
    });
    setSelected(cleared);
    setShowFilters(false);
    if (onApplyFilters) onApplyFilters("");
  };

  return (
    <Container>
      <div>
        <Button
          $NoBgColor
          text={showFilters ? "Ocultar filtros" : "Mostrar filtros"}
          action={() => setShowFilters((v) => !v)}
          color={"#fff"}
          size={"1.5rem"}
          icon={<IonIcon icon={filter} />}
        />
      </div>
      {showFilters && (
        <FilterValuesContainer>
          {Object.entries(filters).map(([filterName, values]) => (
            <FilterGroup key={filterName}>
              <FilterTitle>
                {filterLabels[filterName] || filterName}
              </FilterTitle>
              <FilterValues>
                {values.map((item) => {
                  const value = getValue(item);
                  const label = getLabel(item);
                  return (
                    <Filter
                      key={`${filterName}-${value}`}
                      selected={selected[filterName]?.has(value)}
                      onClick={() => handleToggle(filterName, value)}
                    >
                      {label}
                    </Filter>
                  );
                })}
              </FilterValues>
            </FilterGroup>
          ))}
          <div style={{ display: "flex", gap: "1rem", width: "100%" }}>
            {Object.values(selected).some((set) => set.size > 0) && (
              <Button
                $fullWidth
                text={filterButtonText}
                action={() => {
                  onApplyFilters(buildUrlParams());
                }}
                color={"#fff"}
              />
            )}
            {Object.values(selected).some((set) => set.size > 0) &&
              Object.keys(initialSelected).length === 0 && (
                <Button
                  $fullWidth
                  text="Limpar filtros"
                  action={handleClearFilters}
                  color="#fff"
                  $NoBgColor
                />
              )}
          </div>
        </FilterValuesContainer>
      )}
    </Container>
  );
}
